import { caretAfterDigits, editedNumber } from '@/utils/numberCaret'

/**
 * **`v-keep-number-caret`** — 치는 대로 하이픈을 다시 끼워 그리는 번호 칸(전화·팩스·사업자번호)에서 커서를 친 자리에 둔다.
 * 계산은 `utils/numberCaret`(순수)이 하고, 이 지시자는 칸의 `<input>` 에 붙어 이벤트를 잇기만 한다.
 *
 * - `beforeinput` 에서 고치기 전 글자를 잡는다.
 * - `input`(잡기 단계 — Element 의 처리기보다 먼저)에서 구분 글자만 지운 지우기를 옆 숫자 지우기로 바꾸고, 커서 앞 숫자 개수를
 *   기억한다.
 * - 칸이 다시 그려진 뒤(Element 는 다음 틱에 값을 다시 넣는다) 다음 화면 그리기 직전에 같은 개수의 숫자 뒤로 커서를 옮긴다.
 *
 * 붙이는 곳: `<el-input v-keep-number-caret :value="util.telFieldText(v)" @input="v = util.telDigits($event)">`. 번호가 아닌 글도
 * 받는 칸(사장님 로그인 — 휴대폰·이메일 탭)은 값으로 켜고 끈다: `v-keep-number-caret="loginTab === tabIds.PHONE"`(값이 `false` 면 끔).
 */
const inputOf = (el) => (el.tagName === 'INPUT' ? el : el.querySelector('input'))

const enabledOf = (binding) => binding.value !== false

/** 지우는 키 → `InputEvent.inputType` 이름 */
const KEY_INPUT_TYPES = Object.freeze({ Backspace: 'deleteContentBackward', Delete: 'deleteContentForward' })

export default {
  bind(el, binding) {
    const input = inputOf(el)
    if (!input) return

    el.__keepNumberCaretOn = enabledOf(binding)
    let before = input.value
    // 지우기 방향 — `InputEvent.inputType` 이 없는 입력(합성 이벤트)도 누른 키로 안다
    let keyInputType = null
    const onKeyDown = (event) => {
      before = input.value
      keyInputType = KEY_INPUT_TYPES[event.key] || null
    }
    const onBeforeInput = () => {
      before = input.value
    }
    const onInput = (event) => {
      if (!el.__keepNumberCaretOn) return
      const caret = input.selectionStart == null ? input.value.length : input.selectionStart
      const inputType = event.inputType || keyInputType
      keyInputType = null
      const edited = editedNumber({ before, after: input.value, caret, inputType })
      if (edited.text !== input.value) input.value = edited.text
      if (edited.digitsBefore === null) return

      window.requestAnimationFrame(() => {
        if (document.activeElement !== input) return
        const position = caretAfterDigits(input.value, edited.digitsBefore)
        input.setSelectionRange(position, position)
        before = input.value
      })
    }

    input.addEventListener('keydown', onKeyDown, true)
    input.addEventListener('beforeinput', onBeforeInput, true)
    input.addEventListener('input', onInput, true)
    el.__keepNumberCaretOff = () => {
      input.removeEventListener('keydown', onKeyDown, true)
      input.removeEventListener('beforeinput', onBeforeInput, true)
      input.removeEventListener('input', onInput, true)
    }
  },
  update(el, binding) {
    el.__keepNumberCaretOn = enabledOf(binding)
  },
  unbind(el) {
    if (el.__keepNumberCaretOff) el.__keepNumberCaretOff()
    delete el.__keepNumberCaretOff
    delete el.__keepNumberCaretOn
  },
}
